import api from 'services/api';
import { array, bool, enforce, nullable, num, object, optional, runSchema, str, unknownValue, withDefault, type FieldParser } from 'validation';

const positionSchema: FieldParser<{ x: number | null; y: number | null }> = object<{ x: number | null; y: number | null }>({
  x: nullable(num()),
  y: nullable(num()),
});
import { nodeInstanceChildEdgeSchema, nodeInstanceRowCoreFields, type NodeInstanceChildEdge, type NodeInstanceRowCore } from './types';

// Loader for `GET /node_instances_flat/:id`, which feeds the InstanceViewer.
// Wire shapes re-derived from nap-backend `getNodeInstances.js`
// `getFlattenedParentNodeInstance`: root SELECT ~1416-1654, flattened children
// SELECT ~1782-1905, edges ~2022-2049. Unlike the subtree endpoint, children
// come back as a single FLAT array (no nesting); the graph is reconstructed
// from `child_edges`. See `nodeInstanceWire.ts` for conventions.

/** The focused instance in the flattened view (`NodeInstanceRowCore` + execution details). */
export interface FlatNodeInstance extends NodeInstanceRowCore {
  current_definition_version: number | null;
  definition_changed_after_execution: boolean;
  executed_definition_config: unknown;
  current_definition_config: unknown;
  status_updates: unknown;
  output: unknown;
  outputdetails: unknown;
  files: unknown;
  exit_code: unknown;
  error: unknown;
  worker_pid: number | null;
  pid_start_time: string | null;
  activated_handles: unknown;
  parameters: unknown;
  file_event_zone_name: string | null;
  event_name: string | null;
  // Added by the route after the query.
  can_get_directory: boolean;
  executed_definition_snapshot?: unknown;
  current_definition_snapshot?: unknown;
}

const flatNodeInstanceSchema: FieldParser<FlatNodeInstance> = object<FlatNodeInstance>({
  ...nodeInstanceRowCoreFields,
  current_definition_version: nullable(num()),
  definition_changed_after_execution: bool(),
  executed_definition_config: unknownValue(),
  current_definition_config: unknownValue(),
  status_updates: unknownValue(),
  output: unknownValue(),
  outputdetails: unknownValue(),
  files: unknownValue(),
  exit_code: unknownValue(),
  error: unknownValue(),
  worker_pid: nullable(num()),
  pid_start_time: nullable(str()),
  activated_handles: unknownValue(),
  parameters: unknownValue(),
  file_event_zone_name: nullable(str()),
  event_name: nullable(str()),
  can_get_directory: bool(),
  executed_definition_snapshot: optional(unknownValue()),
  current_definition_snapshot: optional(unknownValue()),
});

/** One flattened descendant node. Carries flow-layout fields; no queue/worker joins, no nesting. */
export interface FlatChildNode {
  node_instance_id: number;
  parent_node_instance_id: number | null;
  node_id: string | null;
  activated_handles: unknown;
  iteration: number | null;
  definition_id: number;
  definition_version: number | null;
  status: string;
  sub_status: string | null;
  scheduled_start_datetime: string | null;
  scheduled_start_timezone: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  runtime_limit_min: number | null;
  runtime_limit_max: number | null;
  duration: number | null;
  created_at: string;
  name: string;
  description: string | null;
  handles: unknown;
  zone_name: string | null;
  type: string;
  sub_type: string | null;
  flow_parent_instance_id: number | null;
  label: string | null;
  spawned_node_instance_id: number | null;
  originating_node_instance_id: number | null;
  submit_and_forget: boolean;
  lock_name: string[] | null;
  has_child_nodes: boolean;
  has_skippable_wait_events: boolean;
  position: { x: number | null; y: number | null };
  parameters: unknown;
  file_event_instance_id: number | null;
  file_event_zone_name: string | null;
  event_name: string | null;
  process_event_name: string | null;
  scheduled_by: string | null;
}

const flatChildNodeSchema: FieldParser<FlatChildNode> = object<FlatChildNode>({
  node_instance_id: num(),
  parent_node_instance_id: nullable(num()),
  node_id: nullable(str()),
  activated_handles: unknownValue(),
  iteration: nullable(num()),
  definition_id: num(),
  definition_version: nullable(num()),
  status: str(),
  sub_status: nullable(str()),
  scheduled_start_datetime: nullable(str()),
  scheduled_start_timezone: nullable(str()),
  start_datetime: nullable(str()),
  end_datetime: nullable(str()),
  runtime_limit_min: nullable(num()),
  runtime_limit_max: nullable(num()),
  duration: nullable(num()),
  created_at: str(),
  name: str(),
  description: nullable(str()),
  handles: unknownValue(),
  zone_name: nullable(str()),
  type: str(),
  sub_type: nullable(str()),
  flow_parent_instance_id: nullable(num()),
  label: nullable(str()),
  spawned_node_instance_id: nullable(num()),
  originating_node_instance_id: nullable(num()),
  submit_and_forget: bool(),
  lock_name: nullable(array(str())),
  has_child_nodes: bool(),
  has_skippable_wait_events: bool(),
  position: withDefault(positionSchema, { x: null, y: null }),
  parameters: unknownValue(),
  file_event_instance_id: nullable(num()),
  file_event_zone_name: nullable(str()),
  event_name: nullable(str()),
  process_event_name: nullable(str()),
  scheduled_by: nullable(str()),
});

export interface FlatNodeInstanceResponse {
  node_instance: FlatNodeInstance | null;
  child_nodes: FlatChildNode[];
  child_edges: NodeInstanceChildEdge[];
}

const flatResponseSchema: FieldParser<FlatNodeInstanceResponse> = object<FlatNodeInstanceResponse>({
  node_instance: nullable(flatNodeInstanceSchema),
  child_nodes: withDefault(array(flatChildNodeSchema), []),
  child_edges: withDefault(array(nodeInstanceChildEdgeSchema), []),
});

/**
 * Loads a node instance flattened with its descendants and flow edges. Throws
 * on a structurally malformed envelope; a missing focused row (`node_instance:
 * null`) is tolerated, and structurally broken children/edges are dropped.
 */
export const getFlatNodeInstance = async (id: number, source = 'getFlatNodeInstance'): Promise<FlatNodeInstanceResponse> => {
  const res = await api.get(`/node_instances_flat/${id}`);
  const parsed = runSchema(flatResponseSchema, res.data, source);
  if (!parsed.ok) {
    throw new Error(`${source}: response failed validation`);
  }
  // The focused row may legitimately be null (missing), but a present-but-drifted
  // identity fails; structurally broken children are dropped.
  return enforce(parsed.value, parsed.warnings, ['node_instance.node_instance_id', 'child_nodes[*].node_instance_id']);
};
